import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PublicSubmissionForm from "@/components/PublicSubmissionForm";
import VolunteerSignupForm from "@/components/foodpulse/VolunteerSignupForm";
import { useCountryFilter } from "@/lib/useCountryFilter";
import CountryFilterBanner from "@/components/CountryFilterBanner";
import { Heart } from "lucide-react";

const CATS = ["all", "housing", "food", "education", "medical", "seniors", "environment"];

export default function Opportunities() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState(params.get("category") || "all");
  const initialTab = params.get("tab");
  const [tab, setTab] = useState(
    initialTab === "submit" ? "submit"
      : initialTab === "signup" ? "signup"
      : "browse"
  );
  const { user } = useAuth();
  const [applying, setApplying] = useState(null);
  const filtered = useCountryFilter(items, ["country", "city"]);

  const load = () => {
    const p = category === "all" ? {} : { category };
    http.get("/opportunities", { params: p }).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, [category]);

  const apply = async (oid) => {
    if (!user) return toast.error("Sign in to apply");
    setApplying(oid);
    try {
      await http.post("/opportunities/apply", { opportunity_id: oid });
      toast.success("Applied — see you there.");
      load();
    } catch (e) { toast.error("Could not apply."); }
    finally { setApplying(null); }
  };

  return (
    <div data-testid="opportunities-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <div className="overline mb-3">VOLUNTEER MARKETPLACE</div>
      <h1 className="font-display text-4xl md:text-5xl max-w-2xl">Show up. Log hours. Earn Social Capital.</h1>

      {/* Prominent "Sign me up" pill above the tabs */}
      {tab !== "signup" && (
        <button type="button" onClick={() => setTab("signup")}
                className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-semibold hover:shadow-md transition-shadow"
                style={{ background: "var(--wofy-primary)" }}
                data-testid="opp-signup-pill">
          <Heart className="w-4 h-4" /> Sign up as a volunteer →
        </button>
      )}

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b overflow-x-auto" style={{ borderColor: "var(--wofy-border)" }}>
        <button data-testid="opp-tab-browse" onClick={() => setTab("browse")} className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors whitespace-nowrap ${tab === "browse" ? "border-b-2" : "opacity-60 hover:opacity-100"}`} style={tab === "browse" ? { borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" } : {}}>Browse opportunities</button>
        <button data-testid="opp-tab-signup" onClick={() => setTab("signup")} className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors whitespace-nowrap ${tab === "signup" ? "border-b-2" : "opacity-60 hover:opacity-100"}`} style={tab === "signup" ? { borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" } : {}}>Sign me up as a volunteer</button>
        <button data-testid="opp-tab-submit" onClick={() => setTab("submit")} className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors whitespace-nowrap ${tab === "submit" ? "border-b-2" : "opacity-60 hover:opacity-100"}`} style={tab === "submit" ? { borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" } : {}}>List a program / opportunity</button>
      </div>

      {tab === "browse" && (
        <>
          <div className="mt-8 flex gap-2 flex-wrap">
            {CATS.map((c) => (
              <button key={c} data-testid={`opp-cat-${c}`} onClick={() => setCategory(c)}
                      className={`rounded-full px-4 py-2 text-sm border transition-colors ${category === c ? "bg-[color:var(--wofy-text)] text-[color:var(--wofy-bg)] border-transparent" : ""}`}
                      style={{ borderColor: "var(--wofy-border)" }}>{c}</button>
            ))}
          </div>

          <CountryFilterBanner shown={filtered.length} total={items.length} noun="opportunities" />

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((o) => (
              <div key={o.id} data-testid={`opp-${o.id}`} className="wofy-card flex flex-col">
                <div className="flex items-center justify-between">
                  <span className="wofy-tag">{o.category}</span>
                  <span className="font-mono text-xs">{o.date}</span>
                </div>
                <div className="font-display text-xl mt-4 leading-snug">{o.title}</div>
                <p className="text-sm mt-2 line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>{o.description}</p>
                <div className="mt-4 flex flex-wrap gap-1">
                  {o.skills.map((s) => <span key={s} className="text-[10px] font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>#{s}</span>)}
                </div>
                <div className="mt-5 text-xs font-mono flex items-center justify-between" style={{ color: "var(--wofy-text-soft)" }}>
                  <span>{o.city.toUpperCase()}</span>
                  <span>{o.hours}h · {o.applied}/{o.slots}</span>
                </div>
                <button data-testid={`apply-${o.id}`} onClick={() => apply(o.id)} disabled={applying === o.id} className="wofy-btn-primary mt-5 justify-center disabled:opacity-60">
                  {applying === o.id ? "Applying…" : "Apply"}
                </button>
              </div>
            ))}
          </div>

          {/* Persistent nudge to sign up as a volunteer */}
          <div className="mt-12 rounded-2xl border-2 border-dashed p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4"
               style={{ borderColor: "var(--wofy-primary)", background: "var(--wofy-bg-soft, #F6F0FF)" }}
               data-testid="opp-signup-nudge">
            <Heart className="w-8 h-8 flex-none" style={{ color: "var(--wofy-primary)" }} />
            <div className="flex-1">
              <div className="font-display text-lg">Ready to help but not tied to one cause?</div>
              <div className="text-sm opacity-80 mt-0.5">
                Sign up once. Get matched to opportunities across food banks, housing,
                education, environment and disaster relief in your city.
              </div>
            </div>
            <button type="button" onClick={() => setTab("signup")}
                    className="wofy-btn-primary whitespace-nowrap"
                    data-testid="opp-signup-nudge-btn">
              Sign me up →
            </button>
          </div>
        </>
      )}

      {tab === "signup" && (
        <div className="mt-10 max-w-3xl" data-testid="opp-signup-panel">
          <VolunteerSignupForm
            siteOrigin="socialpulse"
            heading="Join the Social Pulse Volunteer Network"
            subheading="One profile. Every volunteer opportunity across food, housing, education, health and beyond."
            browseOpportunitiesHref="/opportunities"
          />
        </div>
      )}

      {tab === "submit" && (
        <div className="mt-10 max-w-3xl">
          <PublicSubmissionForm type="volunteer_opportunity" />
        </div>
      )}
    </div>
  );
}
